// src/components/common/ConfirmDialog.jsx
import React, { useEffect } from 'react';

const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message = 'Are you sure you want to proceed?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'danger',
}) => {
  // Handle escape key press
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    
    // Prevent body scrolling when modal is open
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Type-specific button styles
  const buttonStyles = {
    danger: {
      backgroundColor: '#dc2626',
      color: 'white',
      borderColor: '#dc2626',
    },
    warning: {
      backgroundColor: '#d97706',
      color: 'white',
      borderColor: '#d97706',
    },
    info: {
      backgroundColor: '#2563eb',
      color: 'white',
      borderColor: '#2563eb',
    },
    success: {
      backgroundColor: '#059669',
      color: 'white',
      borderColor: '#059669',
    },
  };

  // Type-specific hover styles
  const buttonHoverStyles = {
    danger: {
      backgroundColor: '#b91c1c',
      borderColor: '#b91c1c',
    },
    warning: {
      backgroundColor: '#b45309',
      borderColor: '#b45309',
    },
    info: {
      backgroundColor: '#1d4ed8',
      borderColor: '#1d4ed8',
    },
    success: {
      backgroundColor: '#047857',
      borderColor: '#047857',
    },
  };

  const styles = {
    overlay: {
      position: 'fixed',
      inset: 0,
      zIndex: 50,
      overflowY: 'auto',
      animation: 'fadeIn 0.2s ease-out',
    },
    overlayBackground: {
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.75)',
      transition: 'opacity 0.2s',
    },
    container: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: '1rem',
      textAlign: 'center',
    },
    modal: {
      display: 'inline-block',
      backgroundColor: 'white',
      borderRadius: '0.5rem',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      transform: 'translateY(0)',
      transition: 'all 0.2s',
      maxWidth: '32rem',
      width: '100%',
      animation: 'slideUp 0.3s ease-out',
    },
    content: {
      padding: '1.5rem',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '1rem',
    },
    title: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: '1.5rem',
      color: '#111827',
      margin: 0,
    },
    closeButton: {
      background: 'none',
      border: 'none',
      fontSize: '1.5rem',
      cursor: 'pointer',
      color: '#9ca3af',
      padding: '0.25rem',
      lineHeight: 1,
      transition: 'color 0.2s',
    },
    closeButtonHover: {
      color: '#6b7280',
    },
    messageContainer: {
      marginTop: '0.5rem',
    },
    message: {
      fontSize: '0.875rem',
      color: '#6b7280',
      lineHeight: '1.5',
      margin: 0,
    },
    actions: {
      marginTop: '1.5rem',
      display: 'flex',
      flexDirection: 'row-reverse',
      gap: '0.75rem',
    },
    confirmButton: {
      flex: 1,
      padding: '0.5rem 1rem',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      border: '1px solid transparent',
      cursor: 'pointer',
      transition: 'all 0.2s',
      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    },
    cancelButton: {
      flex: 1,
      padding: '0.5rem 1rem',
      backgroundColor: 'white',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.2s',
      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    },
    cancelButtonHover: {
      backgroundColor: '#f9fafb',
    },
    iconContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '3rem',
      height: '3rem',
      borderRadius: '9999px',
      marginBottom: '1rem',
    },
    iconDanger: {
      backgroundColor: '#fee2e2',
      color: '#dc2626',
    },
    iconWarning: {
      backgroundColor: '#fef3c7',
      color: '#d97706',
    },
    iconInfo: {
      backgroundColor: '#dbeafe',
      color: '#2563eb',
    },
    iconSuccess: {
      backgroundColor: '#d1fae5',
      color: '#059669',
    },
    icon: {
      width: '1.5rem',
      height: '1.5rem',
    },
  };

  const getIconForType = () => {
    switch (type) {
      case 'danger':
        return (
          <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        );
      case 'warning':
        return (
          <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'info':
        return (
          <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'success':
        return (
          <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return null;
    }
  };

  const getIconContainerStyle = () => {
    switch (type) {
      case 'danger':
        return styles.iconDanger;
      case 'warning':
        return styles.iconWarning;
      case 'info':
        return styles.iconInfo;
      case 'success':
        return styles.iconSuccess;
      default:
        return styles.iconInfo;
    }
  };

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div style={styles.overlay} onClick={handleOverlayClick}>
      <div style={styles.overlayBackground} />
      
      <div style={styles.container}>
        <div style={styles.modal}>
          <div style={styles.content}>
            {/* Optional Icon */}
            <div style={{ ...styles.iconContainer, ...getIconContainerStyle() }}>
              {getIconForType()}
            </div>

            {/* Header */}
            <div style={styles.header}>
              <h3 style={styles.title}>{title}</h3>
              <button
                onClick={onClose}
                style={styles.closeButton}
                onMouseEnter={(e) => e.currentTarget.style.color = styles.closeButtonHover.color}
                onMouseLeave={(e) => e.currentTarget.style.color = styles.closeButton.color}
                aria-label="Close dialog"
              >
                ×
              </button>
            </div>

            {/* Message */}
            <div style={styles.messageContainer}>
              <p style={styles.message}>{message}</p>
            </div>

            {/* Actions */}
            <div style={styles.actions}>
              <button
                onClick={handleConfirm}
                style={{
                  ...styles.confirmButton,
                  ...buttonStyles[type],
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = buttonHoverStyles[type].backgroundColor;
                  e.currentTarget.style.borderColor = buttonHoverStyles[type].borderColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = buttonStyles[type].backgroundColor;
                  e.currentTarget.style.borderColor = buttonStyles[type].borderColor;
                }}
              >
                {confirmText}
              </button>
              <button
                onClick={onClose}
                style={styles.cancelButton}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = styles.cancelButtonHover.backgroundColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = styles.cancelButton.backgroundColor;
                }}
              >
                {cancelText}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Add keyframe animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default ConfirmDialog;